import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { UserSelector } from '@client/components/_shared/selectors/users/UserSelector';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { message } from '@client/components/_ui/feedback/message';
import { ComplianceAuditTemplateTypeTag } from '@client/components/compliance-audit-templates/ComplianceAuditTemplateTypeTag';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useUserId } from '@client/hooks/business/user/useUserId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { trpc } from '@client/trpc/client';
import { createFileRoute } from '@tanstack/react-router';
import { Form, Input, Select, Skeleton, Tag } from 'antd';
import { useId } from 'react';

export const Route = createFileRoute('/console/$orgId/tools/compliance-audit/overview/search/add')({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'New compliance audit',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ComplianceAuditAdd />
        </PageController>
    );
}

type ComplianceAudit = {
    insuredName: string;
    policyNumber: string;
    broker: string;
    ar?: string;
    ownerId: bigint;
    // antd Select cannot hold a bigint value — the option value travels as a
    // string and converts back with BigInt() at submit.
    complianceAuditTemplateId?: string;
};

const FormItem = Form.Item<ComplianceAudit>;

// The create form is add-only: an audit's header has no edit surface, and
// evidence enters an audit through the details page's file library, never here.
function ComplianceAuditAdd() {
    const formId = useId();
    const createComplianceAuditMutation = trpc.complianceAudit.createComplianceAudit.useMutation();
    const { trpcUtils } = useTrpc();
    const currentUserId = useUserId();
    const orgId = useOrgId();
    const navigate = useNavigate();

    // The template pin is immutable after creation (steps are cloned from the
    // pinned version), so the select belongs to creation only.
    const templateOptionsQuery = trpc.complianceAudit.listComplianceAuditTemplateOptions.useQuery(undefined, {
        refetchOnWindowFocus: false,
    });

    const [form] = Form.useForm<ComplianceAudit>();

    return (
        <OverlayPage
            title="New compliance audit"
            // Cancel only — a successful create navigates away before the close
            // animation runs, so this never fires on the happy path (see onSuccess).
            afterClose={() => {
                form.resetFields();
                navigate({ moveInMatches: -1 });
            }}
            footer={({ onClose }) => (
                <>
                    <Button type="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        htmlType="submit"
                        form={formId}
                        // No editable form is rendered while the template options are
                        // loading or failed, so block submit in those states.
                        disabled={templateOptionsQuery.isPending || Boolean(templateOptionsQuery.error)}
                        loading={createComplianceAuditMutation.isPending}
                    >
                        Next
                    </Button>
                </>
            )}
        >
            {templateOptionsQuery.error ? (
                <InlineQueryError
                    error={templateOptionsQuery.error}
                    onRetry={() => void templateOptionsQuery.refetch()}
                    title="Couldn't load audit templates"
                />
            ) : templateOptionsQuery.isPending ? (
                <Skeleton active />
            ) : (
                <Form
                    id={formId}
                    layout="vertical"
                    form={form}
                    initialValues={{ ownerId: currentUserId }}
                    onFinish={(values) => {
                        // CUR-668 — submit the version the option displayed, so the
                        // server rejects a template republished mid-form (CONFLICT)
                        // instead of silently pinning a version the user never saw.
                        // The required rule guards the id; the lookup narrows both.
                        const selectedTemplate = templateOptionsQuery.data.rows.find(
                            (row) => String(row.id) === values.complianceAuditTemplateId,
                        );
                        if (!values.complianceAuditTemplateId || !selectedTemplate) {
                            form.setFields([
                                {
                                    name: 'complianceAuditTemplateId',
                                    value: undefined,
                                    errors: ['This template is no longer available — select another template.'],
                                },
                            ]);
                            return;
                        }

                        message.loading('Saving...');

                        // Trim and collapse whitespace-only AR to unset so a blank
                        // input never persists as ''.
                        const trimmedAr = values.ar?.trim() || undefined;

                        createComplianceAuditMutation.mutate(
                            {
                                insuredName: values.insuredName,
                                policyNumber: values.policyNumber,
                                broker: values.broker,
                                ar: trimmedAr,
                                ownerId: values.ownerId,
                                complianceAuditTemplateId: BigInt(values.complianceAuditTemplateId),
                                templateVersion: selectedTemplate.version,
                            },
                            {
                                onSuccess(created) {
                                    message.success('Audit created.');
                                    // The navigation below unmounts this drawer with the
                                    // route change, so `afterClose` never runs — the list
                                    // cache has to be invalidated here or it stays stale.
                                    void trpcUtils.complianceAudit.invalidate();
                                    // `replace` so Back from the details page reaches the
                                    // list, not a re-opened empty create form.
                                    navigate({
                                        to: '/console/$orgId/tools/compliance-audit/details/$complianceAuditId',
                                        params: { orgId, complianceAuditId: created.id },
                                        replace: true,
                                    });
                                },
                                onError(error) {
                                    errorMessage.show(error);
                                    // CUR-668 — a CONFLICT means the template was republished
                                    // mid-form. Refetch the options so the select shows the new
                                    // v{n} and a re-submit carries the fresh version, instead of
                                    // failing with the same conflict until the drawer is reopened.
                                    if (error.data?.code === 'CONFLICT') {
                                        void templateOptionsQuery.refetch();
                                    }
                                },
                            },
                        );
                    }}
                >
                    <FormItem
                        name="complianceAuditTemplateId"
                        label="Audit template"
                        rules={[{ required: true, message: 'Required' }]}
                    >
                        <Select
                            placeholder="Select the template this audit runs with"
                            options={templateOptionsQuery.data.rows.map((row) => ({
                                value: String(row.id),
                                label: (
                                    <span className="inline-flex items-center gap-2">
                                        {row.name}
                                        <ComplianceAuditTemplateTypeTag value={row.type} />
                                        {/* CUR-668 — which published version the audit pins,
                                            styled like the overview list's template column. */}
                                        {row.version != null && (
                                            <Tag variant="outlined" className="m-0">
                                                v{row.version}
                                            </Tag>
                                        )}
                                    </span>
                                ),
                            }))}
                        />
                    </FormItem>
                    <FormItem name="insuredName" label="Insured" rules={[{ required: true, message: 'Required' }]}>
                        <Input placeholder="e.g. Curium Pty Ltd" autoFocus />
                    </FormItem>
                    <FormItem
                        name="policyNumber"
                        label="Policy number"
                        rules={[{ required: true, message: 'Required' }]}
                    >
                        <Input placeholder="e.g. POL-9923" />
                    </FormItem>
                    <FormItem name="broker" label="Broker" rules={[{ required: true, message: 'Required' }]}>
                        <Input placeholder="Broker name" />
                    </FormItem>
                    <FormItem name="ar" label="Authorised representative">
                        <Input placeholder="Optional" />
                    </FormItem>
                    <FormItem name="ownerId" label="Auditor" rules={[{ required: true, message: 'Required' }]}>
                        {/* This should be locked to the current user */}
                        <UserSelector placeholder="Select auditor" readonly />
                    </FormItem>
                </Form>
            )}
        </OverlayPage>
    );
}
